import React from 'react';
import { graphql } from 'react-relay';
import { useNavigate, useLocation } from 'react-router-dom';
import dynamic from 'next/dynamic';
import classes from './PollItem.module.scss';

import { PollItem_poll$data, PollItem_poll$key } from './__generated__/PollItem_poll.graphql';
import PollHeader from '../../molecules/PollHeader/PollHeader';
import LoadingIndicator from '../../atoms/LoadingIndicator/LoadingIndicator';
import Overlay from '../../atoms/Overlay/Overlay';
import useLiveFragment from '../../../relay/useLiveFragment';
import Txt from '../../atoms/Txt/Txt';
import { useRouter } from 'next/router';
import A from 'components/atoms/A/A';

export interface Props {
  pollRef: PollItem_poll$key;
}

const DynamicPollAnswersContainer = dynamic(
  () => import('components/organisms/PollAnswers/PollAnswers'),
);

const fragmentSpec = graphql`
  fragment PollItem_poll on PollQuestion @refetchable(queryName: "PollItemRefetchQuery") {
    id
    startDate
    reward {
      formatted
    }
    description {
      title
    }
    ...PollHeader_data
  }
`;

const PollItem: React.FC<Props> = ({ pollRef }) => {
  const poll: PollItem_poll$data = useLiveFragment(fragmentSpec, pollRef);

  const router = useRouter();
  const isQuiz = !!poll.reward?.formatted;
  const live = poll.startDate ? new Date(poll.startDate) < new Date() : false;
  const [time, setTime] = React.useState<number | null>(null);

  const open = React.useMemo(() => {
    const openPoll = Array.isArray(router.query.poll) ? router.query.poll[0] : router.query.poll;
    return openPoll == poll.id;
  }, [router.query.poll, poll.id]);

  function handleClosePoll() {
    router.push(`${router.pathname}`, undefined, { shallow: true });
  }

  return (
    <div className={classes.root}>
      {open && (
        <Overlay onClose={handleClosePoll} showClose>
          <div className={classes.openPoll}>
            <PollHeader displayTime={time} pollRef={poll} open />
            <React.Suspense fallback={<LoadingIndicator />}>
              <DynamicPollAnswersContainer
                updateDisplayTime={setTime}
                id={poll.id}
                onClose={handleClosePoll}
              />
            </React.Suspense>
          </div>
        </Overlay>
      )}
      <Txt component="h2" className={classes.heading}>
        {isQuiz || !live ? 'Pop Quiz' : 'Daily Poll'}
      </Txt>
      {live ? (
        <A
          to={
            router.asPath.includes('?')
              ? `${router.asPath}&poll=${poll.id}`
              : `${router.asPath}?poll=${poll.id}`
          }
          className={classes.pollPreview}
          data-testid="poll-preview"
        >
          {isQuiz ? (
            <PollHeader pollRef={poll} />
          ) : (
            <div className={classes.pollTitle}>{poll.description.title}</div>
          )}
        </A>
      ) : (
        <div className={classes.pollPreview}>
          <PollHeader pollRef={poll} />
        </div>
      )}
    </div>
  );
};

export default PollItem;
